import { logger } from '@/utils/logger';

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { handleLogin, handleSignup } from '@/utils/authUtils';

export const useAuthFlow = () => {
  const [organizationCode, setOrganizationCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [currentOrganization, setCurrentOrganization] = useState<any>(null);
  const [rememberOrganization, setRememberOrganization] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(false);

  const { signIn, signUp, resetPassword, user, isTwoFactorVerified, setTwoFactorVerified } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Load remembered values on component mount (non-sensitive data only)
  useEffect(() => {
    const savedOrganization = localStorage.getItem('rememberedOrganization');
    const savedEmail = localStorage.getItem('rememberedEmail');
    
    if (savedOrganization) {
      setOrganizationCode(savedOrganization);
      setRememberOrganization(true);
    }
    
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberEmail(true);
    }

    // Auto-populate organization code from subdomain
    const hostname = window.location.hostname;
    if (hostname.endsWith('.tabletally.org') && hostname !== 'app.tabletally.org') {
      const subdomain = hostname.split('.')[0];
      const lookupOrg = async () => {
        const { data } = await (supabase as any)
          .from('v_organizations_public')
          .select('code')
          .eq('subdomain', subdomain)
          .maybeSingle();
        if (data?.code) {
          setOrganizationCode(data.code);
        }
      };
      lookupOrg();
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!isTwoFactorVerified) {
      logger.debug('User authenticated but 2FA not verified. Awaiting 2FA input.');
      return;
    }
    logger.debug('Redirecting to dashboard...');
    navigate('/');
  }, [user, isTwoFactorVerified, navigate]);

  const handleRememberOrganization = (checked: boolean) => {
    setRememberOrganization(checked);
    if (checked && organizationCode) {
      localStorage.setItem('rememberedOrganization', organizationCode);
    } else {
      localStorage.removeItem('rememberedOrganization');
    }
  };

  const handleRememberEmail = (checked: boolean) => {
    setRememberEmail(checked);
    if (checked && email) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
  };

  const handleSignIn = async () => {
    setIsLoading(true);

    // Update localStorage based on checkbox states
    if (rememberOrganization && organizationCode) {
      localStorage.setItem('rememberedOrganization', organizationCode);
    } else {
      localStorage.removeItem('rememberedOrganization');
    }

    if (rememberEmail && email) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    try {
      const result = await handleLogin(
        email,
        password,
        organizationCode,
        signIn,
        setUserProfile,
        setCurrentOrganization,
        setTwoFactorVerified,
        toast
      );
      
      return result;
    } catch (error: any) {
      logger.error('Sign in error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async () => {
    setIsLoading(true);

    try {
      await handleSignup(
        email,
        password,
        fullName,
        organizationCode,
        signUp,
        toast
      );
    } catch (error: any) {
      logger.error('Sign up error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setIsLoading(true);

    try {
      const { error } = await resetPassword(email);
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Password reset email sent",
          description: "Check your email for password reset instructions."
        });
      }
    } catch (error: any) {
      logger.error('Password reset error:', error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // State
    organizationCode,
    setOrganizationCode,
    email,
    setEmail,
    password,
    setPassword,
    fullName,
    setFullName,
    isLoading,
    userProfile,
    setUserProfile,
    currentOrganization,
    setCurrentOrganization,
    rememberOrganization,
    rememberEmail,
    
    // Functions
    handleRememberOrganization,
    handleRememberEmail,
    handleSignIn,
    handleSignUp,
    handlePasswordReset,
    
    // Auth state
    setTwoFactorVerified,
    
    // UI helpers
    toast
  };
};
